// Écran « Santé du radar » : mise en forme des agrégats de `public.radar_health` (journal des passages, sources,
// parcours des recherches de contacts). Fonctions pures, testées dans `radarHealth.test.ts`.

export type RunStatus = "ok" | "partial" | "error";

export type HealthRun = {
  day: string;
  command: string;
  status: RunStatus;
  started_at: string;
  seconds: number;
  counters: Record<string, number>;
  errors: string[];
  llm: number;
  provider: number;
};

export type ContactsDay = {
  day: string;
  runs: number;
  failed: number;
  requests: number;
  llm: number;
  provider: number;
};

export type HealthSource = {
  source: string;
  name: string;
  runs: number;
  errors: number;
  fetched: number;
  inserted: number;
  kept: number;
  kept_recent: number | null;
  kept_before: number | null;
  last_error: string | null;
  failing_since: string | null;
};

export type FunnelStep =
  | "companies"
  | "site"
  | "people"
  | "email"
  | "placed_email";
export const FUNNEL_STEPS: FunnelStep[] = [
  "companies",
  "site",
  "people",
  "email",
  "placed_email",
];

export type FunnelRow = {
  source: string | null;
  country: string | null;
  projects: number;
} & Record<FunnelStep, number>;

export type Health = {
  runs: HealthRun[];
  contacts_runs: ContactsDay[];
  sources: HealthSource[];
  funnel: FunnelRow[];
  outcomes: { step: string; outcome: string; count: number }[];
  rejected: { step: string; reason: string; count: number }[];
};

export type Night = {
  day: string;
  status: RunStatus;
  runs: HealthRun[];
  errors: string[];
  llm: number;
  provider: number;
  contacts?: { runs: number; failed: number; requests: number };
};

const RANK: Record<RunStatus, number> = { ok: 0, partial: 1, error: 2 };

/** Une ligne par nuit (jour de lancement), la plus récente d'abord : commandes dans l'ordre, pire statut, erreurs
 * préfixées de leur commande, coûts additionnés avec ceux de la recherche de contacts du jour. */
export const nights = (runs: HealthRun[], contacts: ContactsDay[]): Night[] => {
  const byDay = new Map<string, Night>();
  const night = (day: string) => {
    let found = byDay.get(day);
    if (!found) {
      found = { day, status: "ok", runs: [], errors: [], llm: 0, provider: 0 };
      byDay.set(day, found);
    }
    return found;
  };
  for (const run of [...runs].sort((a, b) =>
    a.started_at.localeCompare(b.started_at),
  )) {
    const current = night(run.day);
    current.runs.push(run);
    if (RANK[run.status] > RANK[current.status]) current.status = run.status;
    current.errors.push(...run.errors.map((e) => `${run.command} : ${e}`));
    current.llm += Number(run.llm);
    current.provider += Number(run.provider);
  }
  for (const day of contacts) {
    const current = night(day.day);
    current.contacts = {
      runs: day.runs,
      failed: day.failed,
      requests: day.requests,
    };
    current.llm += Number(day.llm);
    current.provider += Number(day.provider);
  }
  return [...byDay.values()].sort((a, b) => b.day.localeCompare(a.day));
};

// Ordre de lecture des compteurs écrits par le worker (`job_runs.counters`) : le volume traité, puis le résultat,
// puis les écarts. Un compteur absent de la liste vient après, par ordre alphabétique.
const COUNTER_ORDER = [
  "projects",
  "confirmed",
  "merged",
  "new_doubts",
  "stale",
  "merge",
  "reject",
  "keep",
  "postponed",
  "extracted",
  "not_relevant",
  "translated",
  "original_name",
  "english_name",
  "missing",
  "searched",
  "completed",
  "facts",
  "recomputed",
  "changed",
  "assessed",
  "email_retries",
  "error",
];

/** Compteurs d'un passage à afficher : les valeurs nulles sont omises (une purge sans rien à effacer n'en montre
 * aucun). */
export const runCounters = (run: HealthRun): [string, number][] => {
  const rank = (key: string) => {
    const index = COUNTER_ORDER.indexOf(key);
    return index < 0 ? COUNTER_ORDER.length : index;
  };
  return Object.entries(run.counters ?? {})
    .filter(([, value]) => Number(value) !== 0)
    .map(([key, value]): [string, number] => [key, Number(value)])
    .sort(([a], [b]) => rank(a) - rank(b) || a.localeCompare(b));
};

export type DeclineReason = "failing" | "collapsed" | "silent" | "never_kept";

// En deçà, une source sans document nouveau ou sans document retenu ne dit encore rien (une nuit creuse arrive).
export const MIN_RUNS = 3;

/** Sources à regarder, des plus graves aux moins graves : en échec, qui ne retiennent plus rien depuis une semaine
 * alors qu'elles retenaient avant, qui n'apportent plus rien de nouveau, qui n'ont jamais rien retenu. */
export const decliningSources = (
  sources: HealthSource[],
): (HealthSource & { reason: DeclineReason })[] => {
  const reasonOf = (source: HealthSource): DeclineReason | null => {
    if (source.failing_since || source.errors >= source.runs) return "failing";
    if (source.runs < MIN_RUNS) return null;
    if (!source.kept_recent && (source.kept_before ?? 0) > 0)
      return "collapsed";
    if (!source.inserted) return "silent";
    if (!source.kept) return "never_kept";
    return null;
  };
  const order: DeclineReason[] = [
    "failing",
    "collapsed",
    "silent",
    "never_kept",
  ];
  return sources
    .map((source) => ({ ...source, reason: reasonOf(source) }))
    .filter(
      (source): source is HealthSource & { reason: DeclineReason } =>
        source.reason !== null,
    )
    .sort((a, b) => order.indexOf(a.reason) - order.indexOf(b.reason));
};

/** Entonnoir des recherches de contacts par source (première source de la fiche) ou par pays, du plus fourni au moins
 * fourni ; chaque étape en part des fiches fouillées. */
export const funnelRows = (rows: FunnelRow[], by: "source" | "country") =>
  rows
    .filter(
      (row) =>
        row[by] !== null &&
        row[by === "source" ? "country" : "source"] === null,
    )
    .map((row) => ({
      key: row[by] as string,
      projects: row.projects,
      counts: Object.fromEntries(
        FUNNEL_STEPS.map((s) => [s, row[s]]),
      ) as Record<FunnelStep, number>,
      share: Object.fromEntries(
        FUNNEL_STEPS.map((s) => [s, row.projects ? row[s] / row.projects : 0]),
      ) as Record<FunnelStep, number>,
    }))
    .sort((a, b) => b.projects - a.projects || a.key.localeCompare(b.key));
